import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Globe,
  Instagram,
  Copy,
  Check,
  ExternalLink,
  Image as ImageIcon,
  Calendar,
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  MessageSquare,
  AlertCircle,
  Smartphone,
  CreditCard,
  Shield,
  Award,
  Eye,
  Download,
  MoreVertical,
  Edit,
  X,
  Save
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import './CollectionArtistDetail.css';

// Helper to add cloudinary transform
const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/${transform}`);
};

const CollectionArtistDetail = () => {
  const { collectionId, artistUuid } = useParams();
  const navigate = useNavigate();
  const context = useOutletContext();
  const { selectedOrganization } = context || {};

  // State
  const [loading, setLoading] = useState(true);
  const [artist, setArtist] = useState(null);
  const [collection, setCollection] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [acceptanceRecord, setAcceptanceRecord] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [error, setError] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    nickname: ''
  });
  const [saving, setSaving] = useState(false);

  // Load all data
  useEffect(() => {
    loadData();
  }, [collectionId, artistUuid]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [
        artistResult,
        collectionResult,
        artworksResult,
        acceptanceResult,
        communicationsResult
      ] = await Promise.all([
        // Artist profile
        supabase
          .from('users')
          .select('*')
          .eq('uuid', artistUuid)
          .single(),
        
        // Collection info
        supabase
          .from('collections')
          .select('collection_id, title, cover_image')
          .eq('collection_id', collectionId)
          .single(),
        
        // Artist's artworks in this collection
        supabase
          .from('collection_vc_mapping')
          .select(`
            vc_id,
            accepted,
            created_at,
            vcs (
              vc_id,
              title,
              cover_image,
              dimensions,
              price,
              status,
              created_at,
              uuid
            )
          `)
          .eq('collection_id', collectionId),
        
        // Get acceptance/waiver status from artist_submission_mapping
        supabase
          .from('artist_submission_mapping')
          .select('*')
          .eq('user_id', artistUuid),
        
        // Placeholder for communications - we'll query by email after getting artist
        Promise.resolve({ data: [], error: null })
      ]);

      if (artistResult.error) throw artistResult.error;
      if (collectionResult.error) throw collectionResult.error;

      setArtist(artistResult.data);
      setCollection(collectionResult.data);

      // Filter artworks to only those by this artist
      const artistArtworks = (artworksResult.data || [])
        .filter(item => item.vcs && item.vcs.uuid === artistUuid)
        .map(item => ({
          ...item.vcs,
          accepted: item.accepted,
          mapping_created_at: item.created_at
        }));
      setArtworks(artistArtworks);

      // Load communications from email_logs by email address
      if (artistResult.data?.email) {
        const emailCommsResult = await supabase
          .from('email_logs')
          .select('*')
          .eq('collection_id', collectionId)
          .eq('user_email', artistResult.data.email)
          .order('sent_at', { ascending: false });
        
        setCommunications(emailCommsResult.data || []);
      }

      // Find acceptance record for this collection's submission
      const submissionResult = await supabase
        .from('submissions_details')
        .select('submission_id')
        .eq('collection_id', collectionId);
      
      if (submissionResult.data && submissionResult.data.length > 0) {
        const submissionIds = submissionResult.data.map(s => s.submission_id);
        const relevantAcceptance = (acceptanceResult.data || []).find(
          acc => submissionIds.includes(acc.submission_id)
        );
        setAcceptanceRecord(relevantAcceptance);
      }

      // Build activity timeline
      await buildActivityTimeline(artistUuid, collectionId, artistArtworks, acceptanceResult.data);

    } catch (err) {
      console.error('Error loading artist detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildActivityTimeline = async (artistId, collId, artworksList, acceptances) => {
    const timeline = [];

    // 1. When artist first submitted to this collection (earliest artwork created_at)
    if (artworksList.length > 0) {
      const firstSubmission = artworksList.reduce((earliest, artwork) => {
        if (!earliest || new Date(artwork.created_at) < new Date(earliest.created_at)) {
          return artwork;
        }
        return earliest;
      }, null);

      if (firstSubmission) {
        timeline.push({
          type: 'submission',
          icon: 'upload',
          title: 'First artwork submitted',
          description: `Submitted "${firstSubmission.title}"`,
          date: firstSubmission.created_at,
          status: 'completed'
        });
      }
    }

    // 2. Terms acceptance
    const relevantAcceptance = acceptances?.find(acc => acc.user_id === artistId && acc.accepted_at);
    if (relevantAcceptance) {
      timeline.push({
        type: 'terms',
        icon: 'check',
        title: 'Terms accepted',
        description: 'Artist accepted submission terms and conditions',
        date: relevantAcceptance.accepted_at,
        status: 'completed'
      });
    }

    // 3. Payment/Purchase
    const paidAcceptance = acceptances?.find(acc => acc.user_id === artistId && acc.purchase_id);
    if (paidAcceptance) {
      timeline.push({
        type: 'payment',
        icon: 'dollar',
        title: 'Submission fee paid',
        description: `Purchase ID: ${paidAcceptance.purchase_id}`,
        date: paidAcceptance.created_at,
        status: 'completed'
      });
    }

    // 4. Waiver granted (paid without purchase_id)
    const waiverAcceptance = acceptances?.find(
      acc => acc.user_id === artistId && acc.paid === true && !acc.purchase_id
    );
    if (waiverAcceptance) {
      timeline.push({
        type: 'waiver',
        icon: 'shield',
        title: 'Fee waiver granted',
        description: 'Submission fee was waived',
        date: waiverAcceptance.updated_at || waiverAcceptance.created_at,
        status: 'completed'
      });
    }

    // 5. Check for artwork acceptances
    const acceptedArtworks = artworksList.filter(aw => aw.accepted);
    if (acceptedArtworks.length > 0) {
      timeline.push({
        type: 'accepted',
        icon: 'award',
        title: 'Artwork accepted',
        description: `${acceptedArtworks.length} artwork(s) accepted into collection`,
        date: new Date().toISOString(), // Would need actual acceptance date
        status: 'completed'
      });
    }

    // Sort by date descending (most recent first)
    timeline.sort((a, b) => new Date(b.date) - new Date(a.date));
    setActivityTimeline(timeline);
  };

  // Open edit modal
  const handleOpenEditModal = () => {
    setEditForm({
      first_name: artist?.first_name || '',
      last_name: artist?.last_name || '',
      nickname: artist?.nickname || ''
    });
    setShowEditModal(true);
  };

  // Save artist profile updates
  const handleSaveArtist = async () => {
    try {
      setSaving(true);
      const { error: updateError } = await supabase
        .from('users')
        .update({
          first_name: editForm.first_name,
          last_name: editForm.last_name,
          nickname: editForm.nickname
        })
        .eq('uuid', artistUuid);

      if (updateError) throw updateError;

      // Update local state
      setArtist(prev => ({
        ...prev,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        nickname: editForm.nickname
      }));
      
      setShowEditModal(false);
    } catch (err) {
      console.error('Error saving artist:', err);
      alert('Failed to save changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmail = async () => {
    if (artist?.email) {
      await navigator.clipboard.writeText(artist.email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };

  const handleSendEmail = () => {
    if (artist?.email && collection) {
      // Navigate to contact email page with artist context and collection for templates
      navigate(`/curator/contacts/email?artistId=${artistUuid}&email=${encodeURIComponent(artist.email)}&name=${encodeURIComponent(artist.nickname || artist.first_name || '')}&collectionId=${collectionId}&collectionName=${encodeURIComponent(collection.name || '')}`);
    }
  };

  const getStatusBadges = () => {
    const badges = [];

    // App installed
    // We'd need to check user_player_ids table
    badges.push({
      key: 'app',
      label: 'App',
      active: artist?.has_app || false,
      color: artist?.has_app ? 'green' : 'gray'
    });

    // Paid
    badges.push({
      key: 'paid',
      label: 'Paid',
      active: !!acceptanceRecord?.purchase_id,
      color: acceptanceRecord?.purchase_id ? 'blue' : 'gray'
    });

    // Submitted
    badges.push({
      key: 'submitted',
      label: 'Submitted',
      active: artworks.length > 0,
      color: artworks.length > 0 ? 'yellow' : 'gray'
    });

    // Terms
    badges.push({
      key: 'terms',
      label: 'Terms',
      active: !!acceptanceRecord?.accepted_at,
      color: acceptanceRecord?.accepted_at ? 'green' : 'yellow',
      icon: acceptanceRecord?.accepted_at ? '✓' : '⏳'
    });

    // Waiver
    const hasWaiver = acceptanceRecord?.paid === true && !acceptanceRecord?.purchase_id;
    badges.push({
      key: 'waiver',
      label: 'Waiver',
      active: hasWaiver,
      color: hasWaiver ? 'green' : 'red',
      icon: hasWaiver ? '✓' : '❌'
    });

    return badges;
  };

  if (loading) {
    return (
      <div className="collection-artist-detail">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading artist details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-artist-detail">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>Error Loading Artist</h3>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft size={16} /> Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="collection-artist-detail">
        <div className="error-state">
          <User size={48} />
          <h3>Artist Not Found</h3>
          <p>The requested artist could not be found.</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft size={16} /> Go Back
          </button>
        </div>
      </div>
    );
  }

  const badges = getStatusBadges();
  const acceptedCount = artworks.filter(a => a.accepted).length;

  return (
    <div className="collection-artist-detail">
      {/* Header with back button */}
      <div className="artist-detail-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={18} />
          <span>Back to Artists</span>
        </button>
        
        {collection && (
          <div className="collection-context">
            <span>in</span>
            <Link to={`/curator/collections/${collectionId}`} className="collection-link">
              {collection.title}
            </Link>
          </div>
        )}
      </div>

      {/* Artist Profile Card - Compact Modern */}
      <div className="artist-profile-card-modern">
        <div className="profile-row">
          <div className="profile-avatar-sm">
            {artist.profile_img ? (
              <img 
                src={addCloudinaryTransform(artist.profile_img, 't_square-150/')} 
                alt={artist.nickname || artist.first_name}
              />
            ) : (
              <User size={24} color="#9ca3af" />
            )}
          </div>
          
          <div className="profile-content">
            <div className="profile-header-row">
              <span className="artist-display-name">
                {artist.nickname || `${artist.first_name || ''} ${artist.last_name || ''}`.trim() || 'Unknown Artist'}
              </span>
              {(artist.first_name || artist.last_name) && artist.nickname && (
                <span className="artist-legal-name">({artist.first_name} {artist.last_name})</span>
              )}
              {artist.handle && (
                <span className="artist-handle">@{artist.handle}</span>
              )}
              <button onClick={handleOpenEditModal} className="edit-btn-inline" title="Edit">
                <Edit size={12} />
              </button>
            </div>
            
            <div className="profile-meta-row">
              {artist.email && (
                <span className="meta-item email">
                  {artist.email}
                  <button onClick={handleCopyEmail} className="copy-inline" title="Copy email">
                    {copiedEmail ? <Check size={11} /> : <Copy size={11} />}
                  </button>
                </span>
              )}
              {artist.phone && (
                <a href={`tel:${artist.phone}`} className="meta-item">{artist.phone}</a>
              )}
              {artist.instagram && (
                <a 
                  href={`https://instagram.com/${artist.instagram.replace('@', '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="meta-item social"
                >
                  <Instagram size={12} />
                </a>
              )}
              {artist.email && (
                <button onClick={handleSendEmail} className="send-email-btn">
                  <Send size={12} />
                  Send Email
                </button>
              )}
            </div>
          </div>

          <div className="profile-stats">
            <div className="stat-item"><strong>{artworks.length}</strong> submitted</div>
            <div className="stat-item"><strong>{acceptedCount}</strong> accepted</div>
            <div className="stat-item"><strong>{communications.length}</strong> emails</div>
          </div>
        </div>

        <div className="profile-badges-row">
          {badges.map(badge => (
            <span 
              key={badge.key}
              className={`badge-pill ${badge.active ? badge.color : 'inactive'}`}
            >
              {badge.icon && <span>{badge.icon}</span>}
              {badge.label}
            </span>
          ))}
        </div>
      </div>

      {/* Edit Artist Modal */}
      {showEditModal && (
        <div className="edit-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="edit-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>Edit Artist Info</h3>
              <button onClick={() => setShowEditModal(false)} className="close-btn">
                <X size={18} />
              </button>
            </div>
            <div className="edit-modal-body">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={e => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="First name"
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={e => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last name"
                />
              </div>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={editForm.nickname}
                  onChange={e => setEditForm(prev => ({ ...prev, nickname: e.target.value }))}
                  placeholder="Display name for exhibitions"
                />
                <span className="form-hint">This name will be displayed on artwork cards and exhibition materials</span>
              </div>
            </div>
            <div className="edit-modal-footer">
              <button onClick={() => setShowEditModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleSaveArtist} disabled={saving} className="btn-save">
                {saving ? 'Saving...' : <>
                  <Save size={14} />
                  Save Changes
                </>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="artist-detail-content">
        {/* Left Column - Artworks */}
        <div className="content-section artworks-section">
          <div className="section-header">
            <h2>
              <ImageIcon size={20} />
              Artworks in Collection
            </h2>
            <span className="count">{artworks.filter(a => a.accepted).length} accepted</span>
          </div>
          
          {artworks.length === 0 ? (
            <div className="empty-state">
              <ImageIcon size={32} />
              <p>No artworks submitted yet</p>
            </div>
          ) : (
            <div className="artworks-list">
              {/* Accepted artworks first */}
              {artworks
                .filter(a => a.accepted)
                .map(artwork => (
                  <Link 
                    key={artwork.vc_id}
                    to={`/curator/artworks/${artwork.vc_id}/edit`}
                    className="artwork-list-item accepted"
                  >
                    <div className="artwork-thumb">
                      {artwork.cover_image ? (
                        <img 
                          src={addCloudinaryTransform(artwork.cover_image, 't_square-150/')} 
                          alt={artwork.title}
                        />
                      ) : (
                        <div className="no-image">
                          <ImageIcon size={20} />
                        </div>
                      )}
                    </div>
                    <div className="artwork-details">
                      <h4>{artwork.title || 'Untitled'}</h4>
                      {artwork.dimensions && <p className="dimensions">{artwork.dimensions}</p>}
                      {artwork.price && (
                        <p className="price">${Number(artwork.price).toLocaleString()}</p>
                      )}
                    </div>
                    <div className="artwork-status">
                      <span className="status-badge accepted">
                        <CheckCircle size={14} /> Accepted
                      </span>
                    </div>
                    <ExternalLink size={16} className="view-icon" />
                  </Link>
                ))}
              
              {/* Non-accepted artworks */}
              {artworks
                .filter(a => !a.accepted)
                .map(artwork => (
                  <Link 
                    key={artwork.vc_id}
                    to={`/curator/artworks/${artwork.vc_id}/edit`}
                    className="artwork-list-item not-accepted"
                  >
                    <div className="artwork-thumb">
                      {artwork.cover_image ? (
                        <img 
                          src={addCloudinaryTransform(artwork.cover_image, 't_square-150/')} 
                          alt={artwork.title}
                        />
                      ) : (
                        <div className="no-image">
                          <ImageIcon size={20} />
                        </div>
                      )}
                    </div>
                    <div className="artwork-details">
                      <h4>{artwork.title || 'Untitled'}</h4>
                      {artwork.dimensions && <p className="dimensions">{artwork.dimensions}</p>}
                      {artwork.price && (
                        <p className="price">${Number(artwork.price).toLocaleString()}</p>
                      )}
                    </div>
                    <span className="not-accepted-label">Not in collection</span>
                    <ExternalLink size={16} className="view-icon" />
                  </Link>
                ))}
            </div>
          )}
        </div>

        {/* Right Column - Timeline & Communications */}
        <div className="content-column-right">
          {/* Activity Timeline */}
          <div className="content-section timeline-section">
            <div className="section-header">
              <h2>
                <Clock size={20} />
                Activity Timeline
              </h2>
            </div>
            
            {activityTimeline.length === 0 ? (
              <div className="empty-state">
                <Clock size={32} />
                <p>No activity recorded yet</p>
              </div>
            ) : (
              <div className="timeline">
                {activityTimeline.map((event, index) => (
                  <div key={index} className={`timeline-item ${event.status}`}>
                    <div className="timeline-icon">
                      {event.icon === 'upload' && <ImageIcon size={14} />}
                      {event.icon === 'check' && <CheckCircle size={14} />}
                      {event.icon === 'dollar' && <DollarSign size={14} />}
                      {event.icon === 'shield' && <Shield size={14} />}
                      {event.icon === 'award' && <Award size={14} />}
                    </div>
                    <div className="timeline-content">
                      <h4>{event.title}</h4>
                      <p>{event.description}</p>
                      <span className="timeline-date">
                        {new Date(event.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Communication History */}
          <div className="content-section communications-section">
            <div className="section-header">
              <h2>
                <MessageSquare size={20} />
                Communication History
              </h2>
              <button onClick={handleSendEmail} className="btn-sm-primary">
                <Send size={14} /> New Email
              </button>
            </div>
            
            {communications.length === 0 ? (
              <div className="empty-state">
                <MessageSquare size={32} />
                <p>No emails sent to this artist yet</p>
                <button onClick={handleSendEmail} className="btn-primary">
                  <Send size={16} /> Send First Email
                </button>
              </div>
            ) : (
              <div className="communications-list">
                {communications.map((comm, index) => (
                  <div key={comm.id || index} className="communication-item">
                    <div className="comm-header">
                      <span className="comm-subject">{comm.subject || 'No Subject'}</span>
                      <span className="comm-date">
                        {new Date(comm.sent_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <p className="comm-preview">
                      {comm.body_text?.substring(0, 120) || 'Email content not available'}
                      {comm.body_text?.length > 120 ? '...' : ''}
                    </p>
                    <div className="comm-status">
                      {comm.status === 'sent' && (
                        <span className="status-sent">
                          <Send size={12} /> Sent
                        </span>
                      )}
                      {comm.status === 'delivered' && (
                        <span className="status-delivered">
                          <Check size={12} /> Delivered
                        </span>
                      )}
                      {(comm.status === 'opened' || comm.open_count > 0) && (
                        <span className="status-opened">
                          <Eye size={12} /> Opened {comm.open_count > 1 ? `(${comm.open_count}x)` : ''}
                        </span>
                      )}
                      {comm.click_count > 0 && (
                        <span className="status-clicked">
                          <ExternalLink size={12} /> Clicked ({comm.click_count}x)
                        </span>
                      )}
                      {(comm.status === 'bounced' || comm.bounced_at) && (
                        <span className="status-failed">
                          <XCircle size={12} /> Bounced
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionArtistDetail;
